import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, TextField, Alert, LinearProgress,
  Button, TableSortLabel, Breadcrumbs, Link, Tabs, Tab, Dialog, DialogTitle, DialogContent, Tooltip
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import MapIcon from "@mui/icons-material/Map";
import TimelineIcon from "@mui/icons-material/Timeline";
import { MapContainer, TileLayer, Marker, Popup, GeoJSON } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const API = process.env.REACT_APP_API_URL || "";
const num = (v) => v != null ? Number(v).toLocaleString("es-MX") : "0";
const headerSx = { fontWeight: 700, bgcolor: "#AF272F", color: "#fff" };
const sortSx = {"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"};

function exportCSV(rows, columns, labels, filename) {
  const header = labels.join(",");
  const csv = [header, ...rows.map(r => columns.map(c => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function ReportesPeriodicos({ token }) {
  const [tab, setTab] = useState(0);
  const [view, setView] = useState("entidad");
  const [data, setData] = useState([]);
  const [selectedEntidad, setSelectedEntidad] = useState(null);
  const [selectedDistrito, setSelectedDistrito] = useState(null);
  const [selectedPlaza, setSelectedPlaza] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("desc");
  const [highlightId, setHighlightId] = useState(null);
  const [modalMapa, setModalMapa] = useState(false);
  const [modalGrafica, setModalGrafica] = useState(false);
  const [modalUsuario, setModalUsuario] = useState(null);
  const [mapaData, setMapaData] = useState({ entregas: [], secciones: [] });
  const [chartData, setChartData] = useState([]);
  const [mapaLoading, setMapaLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const openMapa = async (row) => {
    setModalUsuario(row); setModalMapa(true); setMapaLoading(true);
    try {
      const res = await fetch(`${API}/api/periodicos/entregas/mapa-ruta?plaza_id=${row.plaza_id}`, { headers });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setMapaData({ entregas: json.entregas || [], secciones: json.secciones || [] });
    } catch (e) { setError(e.message); }
    finally { setMapaLoading(false); }
  };

  const openGrafica = async (row) => {
    setModalUsuario(row); setModalGrafica(true); setChartData([]);
    try {
      const res = await fetch(`${API}/api/periodicos/entregas/tendencia?uid=${encodeURIComponent(row.uid)}`, { headers });
      if (!res.ok) return;
      const json = await res.json();
      setChartData((json.data || []).map(r => ({ ...r, fecha: r.fecha?.slice(5) })));
    } catch (e) { setError(e.message); }
  };

  const tabConfig = [
    { label: "Entregas Individuales", base: "periodicos/entregas" },
    { label: "Comités (Pres/Sec)", base: "periodicos/comites" },
    { label: "Periódicos Entregados", base: "periodicos/recibidos" },
  ];

  const getUrl = (t, v, ent, dist, plazaId) => {
    const base = tabConfig[t].base;
    if (v === "entidad") return `${API}/api/${base}/por-entidad`;
    if (v === "distrito") return `${API}/api/${base}/por-distrito?entidad=${ent}`;
    if (v === "ruta") return `${API}/api/${base}/por-ruta?entidad=${ent}&distrito=${dist}`;
    if (v === "seccion") return `${API}/api/${base}/por-seccion-ruta?plaza_id=${plazaId}`;
    return `${API}/api/${base}/por-entidad`;
  };

  const fetchData = useCallback(async (url) => {
    setLoading(true); setError("");
    try {
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setData(json.data || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(getUrl(tab, "entidad")); }, [fetchData, tab]);

  const handleTabChange = (e, newTab) => { setTab(newTab); setView("entidad"); setSelectedEntidad(null); setSelectedDistrito(null); setSelectedPlaza(null); setSearch(""); setSortBy(""); fetchData(getUrl(newTab, "entidad")); };
  const handleEntidadClick = (row) => { setSelectedEntidad(row); setView("distrito"); setSearch(""); fetchData(getUrl(tab, "distrito", row.entidad)); };
  const handleDistritoClick = (row) => { setSelectedDistrito(row); setView("ruta"); setSearch(""); fetchData(getUrl(tab, "ruta", selectedEntidad.entidad, row.distrito)); };
  const handleRutaClick = (row) => { if (tab === 2) { setSelectedPlaza(row); setView("seccion"); setSearch(""); fetchData(getUrl(tab, "seccion", null, null, row.plaza_id)); } };
  const handleBack = () => {
    if (view === "seccion") { setView("ruta"); setSelectedPlaza(null); setSearch(""); fetchData(getUrl(tab, "ruta", selectedEntidad.entidad, selectedDistrito.distrito)); }
    else if (view === "ruta") { setView("distrito"); setSelectedDistrito(null); setSearch(""); fetchData(getUrl(tab, "distrito", selectedEntidad.entidad)); }
    else { setView("entidad"); setSelectedEntidad(null); setSearch(""); fetchData(getUrl(tab, "entidad")); }
  };
  const handleSort = (col) => { if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortBy(col); setSortDir("desc"); } };

  const filtered = data.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return Object.values(r).some(v => String(v || "").toLowerCase().includes(s));
  }).sort((a, b) => {
    if (!sortBy) return 0;
    const av = a[sortBy], bv = b[sortBy];
    const an = Number(av), bn = Number(bv);
    const cmp = (!isNaN(an) && !isNaN(bn)) ? an - bn : String(av || "").localeCompare(String(bv || ""), "es");
    return sortDir === "desc" ? -cmp : cmp;
  });

  const d = (n) => { const dt = new Date(); dt.setDate(dt.getDate() - n); return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getFullYear()).slice(2)}`; };

  const getCols = () => {
    if (tab === 0) { // Entregas individuales
      if (view === "entidad") return [{k:"entidad_nombre",l:"Entidad"},{k:"total_entregas",l:"Total Entregas",a:"right"}];
      if (view === "distrito") return [{k:"distrito",l:"Dto."},{k:"distrito_nombre",l:"Cabecera"},{k:"total_entregas",l:"Total Entregas",a:"right"}];
      // Ruta (último nivel para entregas)
      return [{k:"plaza",l:"Puesto"},{k:"nombre_cot",l:"COT"},{k:"telefono",l:"Teléfono"},{k:"ocupado",l:"Ocupado",a:"center"},{k:"secciones_ruta",l:"Secciones",a:"right"},{k:"total_entregas",l:"Total Entregas",a:"right"},{k:"registros",l:"Registros",a:"right"},{k:"dia_7",l:d(7),a:"right"},{k:"dia_6",l:d(6),a:"right"},{k:"dia_5",l:d(5),a:"right"},{k:"dia_4",l:d(4),a:"right"},{k:"dia_3",l:d(3),a:"right"},{k:"dia_2",l:d(2),a:"right"},{k:"dia_1",l:d(1),a:"right"}];
    }
    if (tab === 1) { // Comités
      if (view === "entidad") return [{k:"entidad_nombre",l:"Entidad"},{k:"total_presidentes",l:"Presidentes",a:"right"},{k:"presidentes_confirmados",l:"Pres. Confirmados",a:"right"},{k:"presidentes_baja",l:"Pres. Baja",a:"right"},{k:"total_secretarios",l:"Secretarios",a:"right"},{k:"secretarios_confirmados",l:"Sec. Confirmados",a:"right"},{k:"secretarios_baja",l:"Sec. Baja",a:"right"}];
      return [{k:"distrito",l:"Dto."},{k:"distrito_nombre",l:"Cabecera"},{k:"total_presidentes",l:"Presidentes",a:"right"},{k:"presidentes_confirmados",l:"Pres. Confirmados",a:"right"},{k:"presidentes_baja",l:"Pres. Baja",a:"right"},{k:"total_secretarios",l:"Secretarios",a:"right"},{k:"secretarios_confirmados",l:"Sec. Confirmados",a:"right"},{k:"secretarios_baja",l:"Sec. Baja",a:"right"}];
    }
    // Periódicos Entregados
    if (view === "entidad") return [{k:"entidad_nombre",l:"Entidad"},{k:"comites",l:"Comités",a:"right"},{k:"total_periodicos",l:"Periódicos Entregados",a:"right"}];
    if (view === "distrito") return [{k:"distrito",l:"Dto."},{k:"distrito_nombre",l:"Cabecera"},{k:"comites",l:"Comités",a:"right"},{k:"total_periodicos",l:"Periódicos Entregados",a:"right"}];
    if (view === "ruta") return [{k:"plaza",l:"Puesto"},{k:"nombre_cot",l:"COT"},{k:"telefono",l:"Teléfono"},{k:"ocupado",l:"Ocupado",a:"center"},{k:"secciones_ruta",l:"Secciones",a:"right"},{k:"comites_con_periodico",l:"Comités c/Periódico",a:"right"},{k:"total_periodicos",l:"Periódicos Entregados",a:"right"}];
    return [{k:"seccion",l:"Sección"},{k:"total_periodicos",l:"Periódicos Entregados",a:"right"}];
  };
  const cols = getCols();

  // Clickable: entidad siempre, distrito si no es comités, ruta solo si tab 2 (tiene secciones)
  const isClickable = (view === "entidad") || (view === "distrito" && tab !== 1) || (view === "ruta" && tab === 2);

  const title = view === "entidad" ? "Reporte Periódicos"
    : view === "distrito" ? `${tabConfig[tab].label} — ${selectedEntidad?.entidad_nombre}`
    : view === "ruta" ? `${tabConfig[tab].label} — ${selectedEntidad?.entidad_nombre} — Dto. ${selectedDistrito?.distrito}`
    : `${selectedPlaza?.nombre_cot || selectedPlaza?.plaza} — Secciones`;

  return (
    <Box sx={{ width: "100%", mx: "auto", px: { xs: 1, sm: 2, md: 3 }, py: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
        {view !== "entidad" && <IconButton onClick={handleBack} sx={{ color: "#AF272F" }}><ArrowBackIcon /></IconButton>}
        <Typography variant="h5" sx={{ color: "#AF272F", fontWeight: 700, flexGrow: 1, fontSize: { xs: "1.2rem", sm: "1.4rem", md: "1.5rem" } }}>{title}</Typography>
        <IconButton onClick={() => fetchData(getUrl(tab, view, selectedEntidad?.entidad, selectedDistrito?.distrito, selectedPlaza?.plaza_id))} title="Actualizar"><RefreshIcon /></IconButton>
        <Button size="small" startIcon={<DownloadIcon />} onClick={() => exportCSV(filtered, cols.map(c=>c.k), cols.map(c=>c.l), `periodicos_${tab}_${view}_${new Date().toISOString().slice(0,10)}.csv`)} sx={{ color: "#AF272F" }}>CSV</Button>
      </Box>

      <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", fontWeight: 600 }, "& .Mui-selected": { color: "#AF272F" }, "& .MuiTabs-indicator": { bgcolor: "#AF272F" } }}>
        {tabConfig.map((t, i) => <Tab key={i} label={t.label} />)}
      </Tabs>

      <Breadcrumbs sx={{ mb: 2 }}>
        <Link underline="hover" sx={{ cursor: "pointer", fontWeight: view === "entidad" ? 700 : 400, color: view === "entidad" ? "#AF272F" : "inherit" }} onClick={() => { setView("entidad"); setSelectedEntidad(null); setSearch(""); fetchData(getUrl(tab, "entidad")); }}>Nacional</Link>
        {(view !== "entidad") && <Link underline="hover" sx={{ cursor: "pointer", fontWeight: view === "distrito" ? 700 : 400, color: "#AF272F" }} onClick={() => { setView("distrito"); setSelectedDistrito(null); setSearch(""); fetchData(getUrl(tab, "distrito", selectedEntidad.entidad)); }}>{selectedEntidad?.entidad_nombre}</Link>}
        {(view === "ruta" || view === "seccion") && <Link underline="hover" sx={{ cursor: "pointer", fontWeight: view === "ruta" ? 700 : 400, color: "#AF272F" }} onClick={() => { setView("ruta"); setSelectedPlaza(null); setSearch(""); fetchData(getUrl(tab, "ruta", selectedEntidad.entidad, selectedDistrito.distrito)); }}>Dto. {selectedDistrito?.distrito}</Link>}
        {view === "seccion" && <Typography color="#AF272F" fontWeight={700}>{selectedPlaza?.nombre_cot || selectedPlaza?.plaza}</Typography>}
      </Breadcrumbs>

      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <SearchIcon sx={{ color: "text.secondary" }} />
        <TextField size="small" placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} sx={{ width: { xs: "100%", sm: 250, md: 300 } }} />
        <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>{filtered.length} registros</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2, "& .MuiLinearProgress-bar": { bgcolor: "#AF272F" } }} />}

      <TableContainer component={Paper} sx={{ maxHeight: "60vh", overflowX: "auto", borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {cols.map(c => (
                <TableCell key={c.k} align={c.a || "left"} sx={headerSx}>
                  <TableSortLabel active={sortBy===c.k} direction={sortBy===c.k?sortDir:"asc"} onClick={()=>handleSort(c.k)} sx={sortSx}>{c.l}</TableSortLabel>
                </TableCell>
              ))}
              {view === "ruta" && tab === 0 && <TableCell align="center" sx={headerSx}>Acciones</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((row, i) => {
              const rowId = row.seccion || row.plaza_id || row.distrito || row.entidad;
              return (
                <TableRow key={i} hover={isClickable} id={`row-${rowId}`}
                  onClick={() => { if (view === "entidad") handleEntidadClick(row); else if (view === "distrito") handleDistritoClick(row); else if (view === "ruta") handleRutaClick(row); }}
                  sx={{...(isClickable ? { cursor: "pointer", "&:hover": { bgcolor: "#fce4ec" } } : {}), bgcolor: highlightId === rowId ? "#fff9c4" : "inherit", transition: "background 0.5s"}}>
                  {cols.map(c => (
                    <TableCell key={c.k} align={c.a || "left"}>
                      {c.k === "ocupado" ? (row[c.k] ? '✅' : '❌')
                      : ["total_entregas","registros","total_presidentes","presidentes_confirmados","presidentes_baja","total_secretarios","secretarios_confirmados","secretarios_baja","comites","total_periodicos","comites_con_periodico","secciones_ruta","dia_1","dia_2","dia_3","dia_4","dia_5","dia_6","dia_7"].includes(c.k)
                        ? num(row[c.k]) : row[c.k]}
                    </TableCell>
                  ))}
                  {view === "ruta" && tab === 0 && (
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      {row.uid && <>
                        <Tooltip title="Ver Mapa"><IconButton size="small" onClick={(e) => { e.stopPropagation(); openMapa(row); }} sx={{ color: "#1565c0" }}><MapIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Gráfica"><IconButton size="small" onClick={(e) => { e.stopPropagation(); openGrafica(row); }} sx={{ color: "#2e7d32" }}><TimelineIcon fontSize="small" /></IconButton></Tooltip>
                      </>}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={cols.length} align="center" sx={{ py: 4, color: "text.secondary" }}>Sin datos</TableCell></TableRow>
            )}
            {filtered.length > 0 && (
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                {cols.map(c => {
                  if (["entidad_nombre","distrito_nombre","plaza","nombre_cot","telefono"].includes(c.k)) return <TableCell key={c.k} sx={{ fontWeight: 700 }}>{["entidad_nombre","plaza"].includes(c.k) ? "TOTAL" : ""}</TableCell>;
                  if (["distrito","seccion","ocupado"].includes(c.k)) return <TableCell key={c.k}></TableCell>;
                  if (["total_entregas","registros","total_presidentes","presidentes_confirmados","presidentes_baja","total_secretarios","secretarios_confirmados","secretarios_baja","comites","total_periodicos","comites_con_periodico","secciones_ruta","dia_1","dia_2","dia_3","dia_4","dia_5","dia_6","dia_7"].includes(c.k))
                    return <TableCell key={c.k} align="right" sx={{ fontWeight: 700 }}>{num(filtered.reduce((s,r) => s + Number(r[c.k]||0), 0))}</TableCell>;
                  return <TableCell key={c.k}></TableCell>;
                })}
                {view === "ruta" && tab === 0 && <TableCell></TableCell>}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal Mapa */}
      <Dialog open={modalMapa} onClose={() => { setModalMapa(false); setMapaData({ entregas: [], secciones: [] }); }} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ bgcolor: "#AF272F", color: "#fff", display: "flex", alignItems: "center" }}>
          🗺️ Entregas — {modalUsuario?.nombre_cot}
          <IconButton onClick={() => { setModalMapa(false); setMapaData({ entregas: [], secciones: [] }); }} sx={{ ml: "auto", color: "#fff" }}>✕</IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 1, height: "70vh" }}>
          {mapaLoading && <LinearProgress />}
          {!mapaLoading && mapaData.entregas.length === 0 && <Alert severity="info" sx={{ mt: 2 }}>Sin entregas con ubicación.</Alert>}
          {mapaData.entregas.length > 0 && (() => {
            const center = [mapaData.entregas[0].latitud, mapaData.entregas[0].longitud];
            return (
              <MapContainer center={center} zoom={13} style={{ height: "100%", width: "100%" }} attributionControl={false} preferCanvas={true}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {mapaData.secciones.map((s, i) => <GeoJSON key={`sec-${s.seccion}`} data={JSON.parse(s.geojson)} style={{ color: "#AF272F", weight: 2, fillOpacity: 0.08 }}><Popup>Sección {s.seccion}</Popup></GeoJSON>)}
                {mapaData.entregas.map((e, i) => (
                  <Marker key={i} position={[e.latitud, e.longitud]}>
                    <Popup><strong>{e.nombre_receptor}</strong><br/>Cantidad: {e.cantidad}<br/>{e.fecha?.slice(0,10)}</Popup>
                  </Marker>
                ))}
              </MapContainer>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Modal Gráfica */}
      <Dialog open={modalGrafica} onClose={() => setModalGrafica(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: "#AF272F", color: "#fff", display: "flex", alignItems: "center" }}>
          📈 Tendencia — {modalUsuario?.nombre_cot}
          <IconButton onClick={() => setModalGrafica(false)} sx={{ ml: "auto", color: "#fff" }}>✕</IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, height: 400 }}>
          {chartData.length === 0 ? <Alert severity="info">Sin datos de tendencia.</Alert> : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="fecha" fontSize={11} />
                <YAxis />
                <RTooltip />
                <Line type="monotone" dataKey="entregas" stroke="#AF272F" strokeWidth={2} dot={{ r: 3 }} name="Entregas" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default ReportesPeriodicos;
